import { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Loader2,
  FileCheck2,
  Eye,
  Download,
  Mail,
  MessageCircle,
  ChevronLeft,
  Copy,
} from "lucide-react";
import { Textarea } from "../../components/ui/textarea";
import { ScoreRing, SevBadge } from "../../components/Bits";
import {
  api,
  errMsg,
  downloadPdf,
  reportLink,
  whatsappLink,
} from "../../lib/api";
import { useInspection } from "../../lib/offline";
import { liveSummary } from "../../lib/summary";
import { SyncBadge } from "./Inspect";
import { useBranding } from "../../lib/branding";

function ReportReady({ ins }) {
  const [share, setShare] = useState(null);
  const [busy, setBusy] = useState("");
  const branding = useBranding();
  useEffect(() => {
    api
      .get(`/inspections/${ins.inspection_id}/share`)
      .then(({ data }) => setShare(data));
  }, [ins.inspection_id]);
  if (!share) return null;
  const link = reportLink(ins.inspection_id, share.token);
  const run = async (k, fn) => {
    setBusy(k);
    try {
      await fn();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy("");
    }
  };
  const B =
    "tap flex h-14 items-center justify-center gap-2 rounded-md text-sm font-bold";
  return (
    <div
      className="rounded-xl border-2 border-emerald-500 bg-white p-6"
      data-testid="report-ready-panel"
    >
      <div className="flex items-center gap-3">
        <FileCheck2 className="h-8 w-8 text-emerald-600" />
        <div>
          <div className="font-display text-2xl font-extrabold">
            REPORT READY
          </div>
          <div className="font-mono text-xs text-slate-500">
            {ins.inspection_id} · generated{" "}
            {new Date(ins.report_generated_at).toLocaleString("en-IN")}
          </div>
        </div>
      </div>
      <div className="mt-5 grid grid-cols-2 gap-2 sm:grid-cols-4">
        <a
          href={link}
          target="_blank"
          rel="noreferrer"
          className={`${B} bg-slate-900 text-white`}
          data-testid="view-report-button"
        >
          <Eye className="h-4 w-4" />
          VIEW REPORT
        </a>
        <button
          onClick={() =>
            run("pdf", async () => {
              await downloadPdf(ins.inspection_id);
            })
          }
          className={`${B} bg-teal-600 text-white`}
          data-testid="download-pdf-button"
        >
          {busy === "pdf" ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Download className="h-4 w-4" />
          )}
          DOWNLOAD PDF
        </button>
        <a
          href={whatsappLink(
            share.customer,
            ins.inspection_id,
            link,
            branding?.company,
          )}
          target="_blank"
          rel="noreferrer"
          className={`${B} bg-[#25D366] text-white`}
          data-testid="share-whatsapp-button"
        >
          <MessageCircle className="h-4 w-4" />
          WHATSAPP
        </a>
        <button
          onClick={() =>
            run("mail", async () => {
              const { data } = await api.post(
                `/inspections/${ins.inspection_id}/email`,
              );
              toast.success(`Report emailed to ${data.to}`);
            })
          }
          className={`${B} border border-slate-300 bg-white`}
          data-testid="email-report-button"
        >
          {busy === "mail" ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Mail className="h-4 w-4" />
          )}
          EMAIL REPORT
        </button>
      </div>
      <button
        onClick={() => {
          navigator.clipboard.writeText(link);
          toast.success("Customer link copied");
        }}
        className="mt-3 flex w-full items-center gap-2 truncate rounded-md bg-slate-50 px-3 py-2 text-left font-mono text-[11px] text-slate-600"
        data-testid="copy-report-link-button"
      >
        <Copy className="h-3.5 w-3.5 shrink-0" />
        {link}
      </button>
    </div>
  );
}

export default function Summary() {
  const { iid } = useParams();
  const { ins, update, status, sync, setIns } = useInspection(iid);
  const [busy, setBusy] = useState(false);
  const s = useMemo(() => (ins ? liveSummary(ins) : null), [ins]);
  if (!ins)
    return (
      <div className="grid place-items-center py-24">
        <Loader2 className="h-6 w-6 animate-spin text-teal-600" />
      </div>
    );
  const done = ins.status === "completed" && ins.report_generated_at;
  const generate = async () => {
    if (status === "OFFLINE") {
      toast.error(
        "You are offline. Observations are saved — reconnect to generate the report.",
      );
      return;
    }
    setBusy(true);
    try {
      await sync();
      const { data } = await api.post(`/inspections/${iid}/complete`);
      setIns(data);
      toast.success("Report generated");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  const pending = s.items.total - s.items.done;
  return (
    <div className="mx-auto max-w-4xl pb-16" data-testid="final-summary">
      <div className="mb-4 flex items-center justify-between">
        <Link
          to={`/inspector/inspection/${iid}`}
          className="flex items-center gap-1 text-sm font-semibold text-slate-600 hover:text-slate-900"
          data-testid="back-to-inspection-link"
        >
          <ChevronLeft className="h-4 w-4" />
          Back to inspection
        </Link>
        <SyncBadge status={status} />
      </div>
      <div className="overline mb-2">Final inspection summary</div>
      <h1 className="font-display text-3xl font-extrabold">
        {ins.property.project} · {ins.property.flat}
      </h1>
      <div className="mt-6 grid gap-4 md:grid-cols-[auto_1fr]">
        <div className="flex flex-col items-center rounded-xl border border-slate-200 bg-white p-6">
          <div className="font-mono text-[10px] tracking-widest text-slate-500">
            OVERALL PROPERTY SCORE
          </div>
          <div className="mt-3">
            <ScoreRing score={s.score} size={150} testid="final-score" />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          {[
            ["Major issues", s.counts.MAJOR, "text-rose-600"],
            ["Minor issues", s.counts.MINOR, "text-amber-600"],
            ["Cosmetic", s.counts.COSMETIC, "text-blue-600"],
            ["Observation", s.counts.OBSERVATION, "text-violet-600"],
          ].map(([l, v, c]) => (
            <div
              key={l}
              className="rounded-xl border border-slate-200 bg-white p-4"
              data-testid={`final-count-${l.split(" ")[0].toLowerCase()}`}
            >
              <div className="font-mono text-[10px] uppercase tracking-widest text-slate-500">
                {l}
              </div>
              <div className={`mt-1 font-display text-3xl font-extrabold ${c}`}>
                {v}
              </div>
            </div>
          ))}
          <div className="col-span-2 rounded-xl border border-slate-200 bg-white p-4 sm:col-span-4">
            <div className="font-mono text-[10px] uppercase tracking-widest text-slate-500">
              Rooms requiring attention
            </div>
            <div className="mt-2 flex flex-wrap gap-1.5">
              {s.roomsAttention.map((r) => (
                <span
                  key={r}
                  className="rounded bg-slate-100 px-2 py-1 text-sm"
                >
                  {r}
                </span>
              ))}
              {!s.roomsAttention.length && (
                <span className="text-sm text-slate-500">None</span>
              )}
            </div>
          </div>
        </div>
      </div>
      <div className="mt-4 grid gap-4 md:grid-cols-2">
        <div className="rounded-xl border border-slate-200 bg-white p-5">
          <div className="overline mb-3">Top 5 priority issues</div>
          <ol className="space-y-2">
            {s.priority.map((d, i) => (
              <li key={d.defect_id} className="flex items-center gap-2 text-sm">
                <span className="font-mono text-xs text-slate-400">
                  {i + 1}.
                </span>
                <span className="flex-1 font-medium">
                  {d.name} <span className="text-slate-500">— {d.room}</span>
                </span>
                <SevBadge sev={d.severity} />
              </li>
            ))}
          </ol>
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-5">
          <div className="overline mb-3">Executive summary (editable)</div>
          <Textarea
            rows={6}
            value={ins.final_notes?.executive_summary || ""}
            placeholder="Leave blank to auto-generate a professional summary from captured data."
            data-testid="executive-summary-input"
            onChange={(e) =>
              update((x) => ({
                ...x,
                final_notes: {
                  ...(x.final_notes || {}),
                  executive_summary: e.target.value,
                },
              }))
            }
          />
        </div>
      </div>
      <div className="mt-6">
        {done ? (
          <ReportReady ins={ins} />
        ) : (
          <div className="rounded-xl border border-slate-200 bg-white p-6">
            {pending > 0 && (
              <p
                className="mb-3 text-sm text-amber-700"
                data-testid="pending-items-warning"
              >
                {pending} items are not yet marked. They will be reported as not
                inspected.
              </p>
            )}
            <button
              onClick={generate}
              disabled={busy}
              className="tap flex h-14 w-full items-center justify-center gap-2 rounded-md bg-slate-900 text-base font-bold text-white hover:bg-teal-700"
              data-testid="generate-pdf-report-button"
            >
              {busy ? (
                <Loader2 className="h-5 w-5 animate-spin" />
              ) : (
                <FileCheck2 className="h-5 w-5" />
              )}{" "}
              COMPLETE INSPECTION & GENERATE REPORT
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
